import { useMemo } from "react";

import { formatMilesDecimal } from "@/services/Utils";
import { getProductPriceUnitLabel } from "@/utils/unitFormatter";
import { formatPriceHistoryDocumentLabel } from "@/utils/formatPriceHistoryDocument";
import { formatDate } from "@/utils/dateFormatter";

import CustomButton from "@/components/desktop/button/CustomButton.jsx";
import EditableNumberCell from "@/components/desktop/list/table/cells/NumberCell/EditableNumberCell.jsx";

import { PRICE_HISTORY_ROW_TYPE } from "../constants";

export default function useProductPriceHistoryColumns({ product, handleDocumentClick, handlePriceSave, beforeSave }) {
	return useMemo(() => {
		return [
			{
				key: "created_at",
				label: "Fecha",
				width: "20%",
				render: (value, row) => <span>{row.isEmptySeason ? "" : formatDate(value)}</span>,
			},
			{
				key: "document",
				label: "Nº Documento",
				width: "40%",
				render: (_, row) => {
					if (row.isEmptySeason) return <span>Sin registros</span>;
					if (row.type === PRICE_HISTORY_ROW_TYPE.MANUAL) return <span>Precio manual</span>;
					if (row.type === PRICE_HISTORY_ROW_TYPE.PREVIOUS_SEASON_AVG) {
						return <span>Precio promedio temporada anterior</span>;
					}
					const documentLabel = formatPriceHistoryDocumentLabel(row);
					return (
						<CustomButton variant="ghost" onClick={() => handleDocumentClick(row)}>
							{documentLabel}
						</CustomButton>
					);
				},
			},
			{
				key: "price",
				label: `Precio promedio ($/${product ? getProductPriceUnitLabel(product.unit_measurement) : "—"})`,
				align: "right",
				render: (value, row) => {
					const price = formatMilesDecimal(value);
					if ((row.active && row.isEmptySeason) || row.type === PRICE_HISTORY_ROW_TYPE.MANUAL) {
						const displayText = row.isEmptySeason ? "Ingresar precio manual" : `$${price}`;
						return (
							<EditableNumberCell
								value={value ?? ""}
								row={{ original: row }}
								fieldName="price"
								getRecordId={(originalRow) => originalRow?.id || "price-history-row"}
								displayFormatter={() => displayText}
								align="right"
								beforeSave={beforeSave}
								onSave={async ({ newValue }) => {
									await handlePriceSave(newValue);
								}}
								required
								saveText="Guardar"
								cancelText="Cancelar"
								savingText="Guardando..."
							/>
						);
					}
					return <span>{`$${price}`}</span>;
				},
			},
		];
	}, [product, handleDocumentClick, handlePriceSave, beforeSave]);
}
